import React from 'react';
import { useEntitlement } from './EntitlementContext';

/**
 * Top-of-page banner that surfaces the shop's billing state when it
 * requires attention (PAST_DUE, READ_ONLY, DOWNGRADED, FROZEN,
 * PENDING_FIRST_PAYMENT). Renders null otherwise — matches plan §8.
 *
 * Colour coding follows the plan: yellow → red escalation.
 */

type BannerKind = ReturnType<typeof useEntitlement>['bannerKind'];

interface BannerStyle {
  background: string;
  border: string;
  color: string;
  title: string;
  body: string;
  ctaLabel: string;
}

const BANNERS: Record<Exclude<BannerKind, null>, BannerStyle> = {
  pending: {
    background: '#eff6ff',
    border: '#2b6eff',
    color: '#1e3a8a',
    title: 'Оплата ещё не подтверждена',
    body:
      'Завершите оплату на странице тарифов, чтобы получить доступ к дашборду.',
    ctaLabel: 'Перейти к оплате',
  },
  past_due: {
    background: '#fef3c7',
    border: '#f59e0b',
    color: '#92400e',
    title: 'Просроченный платёж',
    body:
      'Не удалось списать очередной платёж. Обновите платёжный метод в течение 3 дней, иначе AI и поставки будут отключены.',
    ctaLabel: 'Обновить оплату',
  },
  read_only: {
    background: '#fff1f2',
    border: '#f43f5e',
    color: '#9f1239',
    title: 'Платные модули отключены',
    body:
      'Подписка не оплачена — AI, поставки и SPP временно недоступны. Оплатите в течение 14 дней, чтобы сохранить тариф.',
    ctaLabel: 'Оплатить',
  },
  downgraded: {
    background: '#fee2e2',
    border: '#dc2626',
    color: '#7f1d1d',
    title: 'Тариф понижен до базового',
    body:
      'Подписка не оплачена более 14 дней. Тариф автоматически понижен. Данные сохранены — оплатите, чтобы вернуть полный доступ.',
    ctaLabel: 'Вернуть тариф',
  },
  frozen: {
    background: '#1e293b',
    border: '#0f172a',
    color: '#f8fafc',
    title: 'Магазин заморожен',
    body:
      'Более 90 дней без оплаты — данные сохранены в режиме «только чтение». Свяжитесь с поддержкой для разморозки.',
    ctaLabel: 'Связаться с поддержкой',
  },
};

interface BillingStatusBannerProps {
  /** Where the CTA should route. Defaults to the billing page anchor. */
  billingHref?: string;
}

export function BillingStatusBanner({
  billingHref = '#billing',
}: BillingStatusBannerProps) {
  const { bannerKind } = useEntitlement();
  if (bannerKind === null) return null;
  const style = BANNERS[bannerKind];
  return (
    <div
      role="alert"
      style={{
        background: style.background,
        borderLeft: `4px solid ${style.border}`,
        color: style.color,
        padding: '12px 16px',
        margin: '12px 0',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 300px' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{style.title}</div>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>{style.body}</div>
      </div>
      <a
        href={billingHref}
        style={{
          display: 'inline-block',
          padding: '8px 16px',
          background: style.border,
          color: '#fff',
          borderRadius: 6,
          fontWeight: 600,
          textDecoration: 'none',
          fontSize: 14,
          whiteSpace: 'nowrap',
        }}
      >
        {style.ctaLabel}
      </a>
    </div>
  );
}
